import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserNotificationResponse, UsersService } from "../../api";

dayjs.extend(relativeTime);

type TopbarNotificationsProps = {
  notifications?: UserNotificationResponse[];
  onMarkedRead?: () => void;
};

function formatWhen(value?: string | null) {
  if (!value) return "";
  const date = dayjs(value);
  return date.isValid() ? date.fromNow() : "";
}

export default function TopbarNotifications({
  notifications = [],
  onMarkedRead,
}: TopbarNotificationsProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications]
  );

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    UsersService.postApiUsersNotifications()
      .then(() => onMarkedRead?.())
      .catch(() => undefined);
  };

  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (notification: UserNotificationResponse) => {
    handleClose();
    if (!notification.link) return;

    try {
      const url = new URL(notification.link, window.location.origin);
      if (url.origin === window.location.origin) {
        navigate(`${url.pathname}${url.search}${url.hash}`);
        return;
      }
    } catch {
      // fall through to full navigation
    }
    window.location.href = notification.link;
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          className="app-topbar__notify-btn"
          onClick={handleOpen}
          aria-label="notifications"
          aria-controls={open ? "notifications-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open}
          size="small"
        >
          <Badge
            color="error"
            badgeContent={unreadCount}
            max={99}
            overlap="circular"
          >
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id="notifications-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 0,
          className: "app-topbar__menu-paper app-topbar__notifications-paper",
        }}
      >
        <Box className="app-topbar__notifications-header">
          <Typography variant="subtitle2" fontWeight={600}>
            Notifications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {unreadCount > 0
              ? `${unreadCount} unread`
              : notifications.length > 0
                ? "You're all caught up"
                : "No new updates"}
          </Typography>
        </Box>
        <Divider />

        <Box className="app-topbar__notifications-list">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <button
                key={notification.id}
                type="button"
                className={`app-topbar__notification${
                  notification.isRead ? "" : " is-unread"
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <span
                  className="app-topbar__notification-dot"
                  aria-hidden
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    className="app-topbar__notification-text"
                  >
                    {notification.content}
                  </Typography>
                  {notification.createdAt && (
                    <Typography variant="caption" color="text.secondary">
                      {formatWhen(notification.createdAt)}
                    </Typography>
                  )}
                </Box>
              </button>
            ))
          ) : (
            <Box className="app-topbar__notifications-empty">
              <NotificationsNoneOutlinedIcon color="disabled" />
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}
